import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Clock, DownloadSimple, CalendarBlank, UsersThree, ArrowSquareOut } from "@phosphor-icons/react";
import { toLocalISODate } from "../lib/dateUtil";

import DateInput from "../components/DateInput";
function fmtHours(mins) {
  if (!mins) return "0h";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function presetRange(kind) {
  const now = new Date();
  const fmt = (d) => toLocalISODate(d);
  if (kind === "week") {
    const monday = new Date(now);
    const day = (monday.getDay() + 6) % 7;
    monday.setDate(now.getDate() - day);
    return { start: fmt(monday), end: fmt(now) };
  }
  if (kind === "month") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: fmt(first), end: fmt(now) };
  }
  if (kind === "last_week") {
    const end = new Date(now);
    const day = (end.getDay() + 6) % 7;
    end.setDate(end.getDate() - day - 1);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    return { start: fmt(start), end: fmt(end) };
  }
  return { start: "", end: "" };
}

export default function TimesheetsPage() {
  const [team, setTeam] = useState([]);
  const [operativeId, setOperativeId] = useState("");
  const [range, setRange] = useState(presetRange("week"));
  const [preset, setPreset] = useState("week");
  const [data, setData] = useState({ rows: [], by_operative: [], totals: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/in-house/team").then(({ data }) => setTeam(data.team || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (operativeId && operativeId !== "__all__") qs.set("operative_id", operativeId);
      if (range.start) qs.set("start", range.start);
      if (range.end) qs.set("end", range.end);
      const { data } = await API.get(`/admin/timesheets?${qs.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load timesheets");
    } finally {
      setLoading(false);
    }
  }, [operativeId, range]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (kind) => { setPreset(kind); setRange(presetRange(kind)); };

  const exportCsv = async () => {
    const qs = new URLSearchParams();
    if (operativeId && operativeId !== "__all__") qs.set("operative_id", operativeId);
    if (range.start) qs.set("start", range.start);
    if (range.end) qs.set("end", range.end);
    try {
      const res = await API.get(`/admin/timesheets/export.csv?${qs.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `timesheets-${range.start || "all"}-${range.end || "all"}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV export failed");
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="timesheets-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Clock className="h-7 w-7 text-blue-600" /> Timesheets
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            On-site hours captured from geotagged Arrive/Complete events. Totals roll up per operative.
          </p>
        </div>
        <Button onClick={exportCsv} variant="outline" data-testid="timesheets-export-btn">
          <DownloadSimple className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="flex gap-1">
          {[["week", "This week"], ["last_week", "Last week"], ["month", "This month"], ["custom", "Custom"]].map(([k, lbl]) => (
            <button key={k} onClick={() => applyPreset(k)}
                    className={`px-3 py-1.5 text-xs rounded ${preset === k ? "bg-primary text-primary-foreground" : "bg-slate-100 dark:bg-slate-800 hover:bg-slate-200"}`}
                    data-testid={`timesheets-preset-${k}`}>
              {lbl}
            </button>
          ))}
        </div>
        <div>
          <Label className="text-xs">Start</Label>
          <DateInput value={range.start} onChange={(e) => { setPreset("custom"); setRange({ ...range, start: e.target.value }); }}
                 className="w-[150px]" data-testid="timesheets-start" />
        </div>
        <div>
          <Label className="text-xs">End</Label>
          <DateInput value={range.end} onChange={(e) => { setPreset("custom"); setRange({ ...range, end: e.target.value }); }}
                 className="w-[150px]" data-testid="timesheets-end" />
        </div>
        <div className="min-w-[220px]">
          <Label className="text-xs">Operative</Label>
          <Select value={operativeId || "__all__"} onValueChange={(v) => setOperativeId(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="timesheets-operative-filter"><SelectValue placeholder="All operatives" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All operatives</SelectItem>
              {team.map((u) => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total hours" value={fmtHours(data.totals?.minutes || 0)} />
        <Stat label="Completed hours" value={fmtHours(data.totals?.completed_minutes || 0)} />
        <Stat label="Entries" value={data.totals?.entries || 0} />
        <Stat label="Operatives" value={(data.by_operative || []).length} />
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <UsersThree className="h-4 w-4 text-slate-500" />
          <h2 className="text-sm font-semibold">By operative</h2>
        </div>
        {data.by_operative.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">No on-site time captured in this range.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Operative</th>
                <th className="text-right px-4 py-2">Total on-site</th>
                <th className="text-right px-4 py-2 hidden sm:table-cell">Completed</th>
                <th className="text-right px-4 py-2 hidden sm:table-cell">Open</th>
                <th className="text-right px-4 py-2">Jobs</th>
              </tr>
            </thead>
            <tbody>
              {data.by_operative.map((b) => (
                <tr key={b.operative_id} className="border-t border-border" data-testid={`timesheets-op-${b.operative_id}`}>
                  <td className="px-4 py-2 font-medium">{b.operative_name}</td>
                  <td className="px-4 py-2 text-right font-semibold">{fmtHours(b.total_minutes)}</td>
                  <td className="px-4 py-2 text-right hidden sm:table-cell text-slate-500">{fmtHours(b.completed_minutes)}</td>
                  <td className="px-4 py-2 text-right hidden sm:table-cell">
                    {b.open_sessions > 0 ? <Badge className="bg-blue-100 text-blue-800">{b.open_sessions} on site</Badge> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-500">{b.jobs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <CalendarBlank className="h-4 w-4 text-slate-500" />
          <h2 className="text-sm font-semibold">Entries</h2>
          <Badge variant="secondary" className="ml-auto">{data.rows.length}</Badge>
        </div>
        {loading ? (
          <p className="p-6 text-center text-sm text-slate-400">Loading…</p>
        ) : data.rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">No entries found for the selected filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Operative</th>
                  <th className="text-left px-4 py-2">Job</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Arrived</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Completed</th>
                  <th className="text-right px-4 py-2">On-site</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={`${r.entity_id}-${i}`} className="border-t border-border">
                    <td className="px-4 py-2">{r.date}</td>
                    <td className="px-4 py-2 font-medium">{r.operative_name || "—"}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col">
                        <span className="font-medium truncate max-w-[260px]">{r.title}</span>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wide">{r.entity_type.replace("_", " ")} · {r.reference}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell text-slate-600">{new Date(r.arrived_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-4 py-2 hidden md:table-cell text-slate-600">
                      {r.completed_at ? new Date(r.completed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : <span className="text-amber-600 text-xs">On site</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold">{r.minutes != null ? fmtHours(r.minutes) : "—"}</td>
                    <td className="px-2 py-2">
                      <Link to={`/${r.entity_type === "emergency_job" ? "emergency-jobs" : "jobs"}/${r.entity_id}`}
                            className="text-slate-400 hover:text-slate-700" title="Open job">
                        <ArrowSquareOut className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}
